import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { BackLink, PageHeader } from '@/layouts/AppLayout';
import type { CrmModule, PermissionScope } from '@diverse-crm/shared-types';

const MODULES: { key: CrmModule; label: string }[] = [
  { key: 'clients', label: 'Clients' },
  { key: 'hotels', label: 'Hotels' },
  { key: 'contracted_rates', label: 'Contracted Rates' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'vouchers', label: 'Vouchers' },
  { key: 'payments', label: 'Payments' },
  { key: 'commissions', label: 'Commissions' },
  { key: 'reports', label: 'Reports' },
  { key: 'audit_log', label: 'Audit Log' },
];

type PermissionAction = 'canCreate' | 'canRead' | 'canUpdate' | 'canDelete';
const ACTIONS: { key: PermissionAction; label: string }[] = [
  { key: 'canCreate', label: 'Create' },
  { key: 'canRead', label: 'Read' },
  { key: 'canUpdate', label: 'Update' },
  { key: 'canDelete', label: 'Delete' },
];

interface Permission {
  module: CrmModule;
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  scope: PermissionScope;
}

interface UserSummary {
  id: string;
  fullName: string;
  email: string;
  role: string;
}

function emptyRow(module: CrmModule): Permission {
  return { module, canCreate: false, canRead: false, canUpdate: false, canDelete: false, scope: 'own' };
}

function mergeWithDefaults(serverPerms: Permission[]): Permission[] {
  return MODULES.map(({ key }) => {
    const found = serverPerms.find((p) => p.module === key);
    return found ?? emptyRow(key);
  });
}

export function UserPermissionsPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [dirty, setDirty] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['users', id],
    queryFn: () => api.get<UserSummary>(`/users/${id}`),
    enabled: !!id,
  });

  const { data: serverPerms, isLoading } = useQuery({
    queryKey: ['users', id, 'permissions'],
    queryFn: () => api.get<Permission[]>(`/users/${id}/permissions`),
    enabled: !!id,
  });

  const [perms, setPerms] = useState<Permission[]>([]);

  // Sync server data into local state on first load
  if (serverPerms && perms.length === 0) {
    setPerms(mergeWithDefaults(serverPerms));
  }

  const mutation = useMutation({
    mutationFn: (permissions: Permission[]) =>
      api.put(`/users/${id}/permissions`, { permissions }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users', id, 'permissions'] });
      setDirty(false);
      toast.success('Permissions saved');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to save permissions';
      toast.error(msg);
    },
  });

  const toggleAction = (module: CrmModule, action: PermissionAction) => {
    setDirty(true);
    setPerms((prev) =>
      prev.map((p) => (p.module === module ? { ...p, [action]: !p[action] } : p)),
    );
  };

  const setScope = (module: CrmModule, scope: PermissionScope) => {
    setDirty(true);
    setPerms((prev) => prev.map((p) => (p.module === module ? { ...p, scope } : p)));
  };

  if (isLoading || perms.length === 0) {
    return <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>;
  }

  return (
    <div>
      <PageHeader
        title={`Permissions — ${user?.fullName ?? '…'}`}
        description={user?.email}
      />
      <div className="p-6 max-w-3xl">
        <BackLink to={`/settings/users/${id}`} label="Back to profile" />

        {user?.role === 'super_admin' && (
          <div className="mb-4 rounded-md bg-tangerine-50 border border-tangerine-200 px-4 py-3 text-sm text-tangerine-800">
            Super admins bypass all permission checks — this matrix is not enforced for them.
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Module permissions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th className="px-4 py-3 text-left font-medium text-neutral-500 w-40">Module</th>
                    {ACTIONS.map((a) => (
                      <th key={a.key} className="px-3 py-3 text-center font-medium text-neutral-500 w-20">
                        {a.label}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left font-medium text-neutral-500 w-28">Scope</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {perms.map((perm) => (
                    <tr key={perm.module} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-neutral-700">
                        {MODULES.find((m) => m.key === perm.module)?.label}
                      </td>
                      {ACTIONS.map((action) => (
                        <td key={action.key} className="px-3 py-3 text-center">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={perm[action.key]}
                              onCheckedChange={() => toggleAction(perm.module, action.key)}
                            />
                          </div>
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <Select
                          value={perm.scope}
                          onValueChange={(v) => setScope(perm.module, v as PermissionScope)}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="own">Own</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-neutral-200 px-4 py-3 flex justify-end">
              <Button
                onClick={() => mutation.mutate(perms)}
                disabled={!dirty || mutation.isPending}
              >
                {mutation.isPending ? 'Saving…' : 'Save permissions'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
